import React, { ReactElement, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { Verify } from '@common-modules/registration-checkout';
import CHECKOUT_CONSTANT from '../../constants/checkout';
import { useDispatch, useSelector } from 'react-redux';
import { Column, Grid, Row, Spacing, Text, TextLink } from '@dls/web';
import { BaseState } from '../../types/state.types';
import { WorryFreeData } from '../../types/user.types';
import { trans as t } from '../../helpers/localisation';
import AUTH_CONSTANTS from '../../constants/auth';
import getIsRoiMismatch from '../../helpers/is-roi-mismatch';
import {
  authActions,
  NEW_NEW_CUSTOMER,
  OTP_EXCEED_LIMIT_ERROR,
  OTP_GENERIC_ERROR,
  OTP_RESEND_SUCCESS,
  OTP_SENT,
  OTP_TIMEOUT_ERROR,
  OTP_VERIFIED,
  OTP_VERIFY_ERROR,
  WORRY_FREE_FA_CHECK
} from '@detox/actions';
import { navigation } from '../../middlewares/navigation-constants';
import notifications from '../../config/notifications';
import { getUIAMLoginUrl } from '../../config';
import { useLoader } from '../../hooks/useLoader';
import { SkeletonType } from '../SkeletonLoader';
import { ACTION_TYPES } from '../../constants/actions';
import CCEMisMatchError from '../CCEPrompt/CCEMisMatchErrorModal';
import { APP_TYPE_ANY } from '../../types/common.types';
import { getUIAMEnabledWithToken } from '../../selectors';
import { AbFeatureNames } from '../../constants/ab-feature-names';
import { useABTesting } from '@wec-core/ab-testing';

type PropTypes = {
  onePassTargetUrl?: string;
};
export const WorryFreeLogin = (props: PropTypes): ReactElement => {
  const { onePassTargetUrl = navigation.CHOOSE_NUMBER_PAGE } = props;
  const [errorMessage, setErrorMessage] = useState('');
  const [authStatus, setAuthStatus] = useState(
    AUTH_CONSTANTS.CHECKOUT_VERIFY_ID
  );
  const [cceMismatch, setCceMismatch] = useState(false);
  const { user, auth, roiValidatedData, apigeeTokenEnabled } = useSelector<
    BaseState,
    Partial<BaseState>
  >(state => {
    return {
      user: state.user,
      auth: state.auth,
      roiValidatedData: state.iphone?.validateSkuData,
      apigeeTokenEnabled: getUIAMEnabledWithToken(state)
    };
  });
  const { worryFreeData } = user;
  const worryFreeStatus = auth?.worryFree?.status;
  const isLoginStage = auth.status === AUTH_CONSTANTS.WORRY_FREE_LOGIN;
  const dispatch = useDispatch();
  const { isFeatureAllowed: abTestingEshopOtpApiEnabled } = useABTesting({
    featureName: AbFeatureNames.ESHOP_OTP_API
  });
  const { isFeatureAllowed: isWorryFreeReCaptchaEnabled } = useABTesting({
    featureName: AbFeatureNames.ESHOP_RECAPTCHA
  });
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { Loader } = useLoader({
    options: { type: SkeletonType.centreCard },
    skeletonLoadings: [user.loading]
  });

  useEffect(() => {
    if (
      apigeeTokenEnabled &&
      (!auth?.headers || !Object.keys(auth.headers)?.length)
    ) {
      dispatch(authActions.getOnePassHeaders());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apigeeTokenEnabled]);

  useEffect(() => {
    switch (worryFreeStatus) {
      case WORRY_FREE_FA_CHECK:
      case NEW_NEW_CUSTOMER: {
        setErrorMessage(notifications[NEW_NEW_CUSTOMER].text as string);
        break;
      }
      case OTP_SENT:
        setErrorMessage('');
        setAuthStatus(AUTH_CONSTANTS.WORRY_FREE_OTP);
        break;
      case OTP_RESEND_SUCCESS:
        setAuthStatus(AUTH_CONSTANTS.WORRY_FREE_OTP);
        break;
      case OTP_VERIFIED:
        break;
      case OTP_EXCEED_LIMIT_ERROR:
        setAuthStatus(AUTH_CONSTANTS.OTP_LIMIT_EXCEEDED);
        setErrorMessage(t('WORRY_FREE_GENERIC_ERROR') as string);
        break;
      case AUTH_CONSTANTS.OTP_MISMATCH_ERROR:
        setErrorMessage(t('WORRY_FREE_OTP_MISMATCH_ERROR') as string);
        break;
      case OTP_GENERIC_ERROR: {
        setErrorMessage(t('WORRY_FREE_GENERIC_ERROR') as string);
        setAuthStatus(AUTH_CONSTANTS.OTP_GENERIC_ERROR);
        break;
      }
      case OTP_VERIFY_ERROR:
        setErrorMessage(t('VERIFICATION_OTP_INVALID_OTP_ERROR_MSG') as string);
        break;
      case OTP_TIMEOUT_ERROR:
        setErrorMessage(t('VERIFICATION_OTP_EXPIRED_OTP_ERROR_MSG') as string);
        break;
    }
  }, [worryFreeStatus]);

  const requestOtp = async (data: WorryFreeData) => {
    if (isWorryFreeReCaptchaEnabled && !executeRecaptcha) {
      return;
    }
    let recaptchaData = {};
    if (isWorryFreeReCaptchaEnabled) {
      const recaptchaToken = await executeRecaptcha('worry_free_login');
      recaptchaData = {
        captchaResponseToken: recaptchaToken,
        action: 'worry_free_login'
      };
    }

    const worryFreeParams = {
      cleanWorryFree: !data.triggeredResend,
      mobileNumber: data.mobileNumberWithoutFormat,
      indentType: data.passType,
      indentValue: data.id,
      ...recaptchaData
    };
    const hasRoiDataMismatch = getIsRoiMismatch(data.id, roiValidatedData);
    if (hasRoiDataMismatch) {
      return setCceMismatch(true);
    }

    dispatch(
      authActions.sendOtp(
        worryFreeParams as APP_TYPE_ANY,
        abTestingEshopOtpApiEnabled
      )
    );
    dispatch({
      type: ACTION_TYPES.AUTH.SET_WORRY_FREE_DATA,
      data
    });
  };

  const verifyOtp = loginDetails => {
    dispatch(authActions.resetWorryFreeStatus({}));
    setErrorMessage('');
    dispatch(
      authActions.verifyOtp(
        {
          otpCode: loginDetails.otp,
          otp: loginDetails.otp,
          mobileNumber: loginDetails.mobileNumberWithoutFormat,
          indentType: loginDetails.passType,
          indentValue: loginDetails.id
        },
        abTestingEshopOtpApiEnabled
      )
    );
  };

  const handleOnePassLogin = () => {
    window.open(getUIAMLoginUrl(onePassTargetUrl), '_self');
  };

  const renderGenericError = () => {
    return (
      <>
        <Text type="pageTitle">{t('GENERIC_ERROR_TITLE')}</Text>
        <Spacing topBottom={2} responsive={false}>
          <Text type={'body'}>{t('WORRY_FREE_GENERIC_ERROR_MESSAGE')}</Text>
        </Spacing>
        <TextLink onClick={handleOnePassLogin}>{t('LOGIN_ONE_PASS')}</TextLink>
      </>
    );
  };

  const renderOtpLogin = () => {
    return (
      <Verify
        authStatus={isLoginStage ? AUTH_CONSTANTS.WORRY_FREE_NRIC : authStatus}
        data={{
          mainData: CHECKOUT_CONSTANT.MAINDATA,
          data: { ...worryFreeData }
        }}
        errorMessage={errorMessage}
        isLoading={auth.isLoading}
        configs={{
          otpTimeInterval: 30,
          onePassLoginRequired: true,
          disabledFields: []
        }}
        callbacks={{
          onNricVerified: requestOtp,
          onOtpVerified: verifyOtp,
          onOnePassLoginClick: handleOnePassLogin
        }}
        localisation={{
          VERIFICATION_TITLE: t('LOGIN_WITH_OTP'),
          VERIFICATION_SUBTITLE: null,
          VERIFICATION_GET_SMS_OTP: t('VERIFICATION_GET_SMS_OTP'),
          VERIFICATION_REQUIREMENTS: t('WORRY_FREE_LOGIN_LINER'),
          VERIFICATION_OTP_VERIFY_WITH_ONEPASS_TEXT: null,
          VERIFICATION_OTP_VERIFY_WITH_ONEPASS: t(
            'VERIFICATION_OTP_VERIFY_WITH_ONEPASS'
          ),
          VERIFICATION_OTP_LIMIT_EXCEEDED_TITLE: t('OTP_REQUESTS_REACHED'),
          VERIFICATION_MOBILE_NUMBER_LABEL: t(
            'VERIFICATION_MOBILE_NUMBER_LABEL'
          )
        }}
      />
    );
  };

  const render = () => {
    switch (true) {
      case user.loading || !auth.status:
        return <Loader />;

      case !isLoginStage &&
        auth.worryFree?.status === AUTH_CONSTANTS.OTP_GENERIC_ERROR: {
        return renderGenericError();
      }
      default:
        return renderOtpLogin();
    }
  };

  return (
    <Spacing bottom={5}>
      <Grid>
        <Row>
          <Column>{render()}</Column>
        </Row>
      </Grid>
      {cceMismatch && (
        <CCEMisMatchError
          proceedClicked={() => {
            setCceMismatch(false);
          }}
        />
      )}
    </Spacing>
  );
};
